import { useMemo } from 'react'

import type { DragPayload } from '@/features/bidding/types/bidLadderInput'
import type { DragPayloadSymmetrical } from '@/features/bidding/types/bidLadderInputSymmetrical'
import type { CascadeMarketId } from '@/features/bidding/types/market'
import { getBidLevelConfig } from '@/features/bidding/utils/marketCapabilities'

import type { UseBidLadderInputStateReturn } from './useBidLadderInputState'
import { useBidLadderInputState } from './useBidLadderInputState'
import type { UseBidLadderInputStateSymmetricalReturn } from './useBidLadderInputStateSymmetrical'
import { useBidLadderInputStateSymmetrical } from './useBidLadderInputStateSymmetrical'

export type UseBidLadderStateReturn =
  | {
      isSymmetrical: false
      state: UseBidLadderInputStateReturn
      assignGroups: (levelId: string, payload: DragPayload | DragPayloadSymmetrical) => void
      unassignRegulationObjectGroup: (levelId: string, regulationObjectCode: string) => void
      assignedGroupUuids: Set<string>
    }
  | {
      isSymmetrical: true
      state: UseBidLadderInputStateSymmetricalReturn
      assignGroups: (levelId: string, payload: DragPayload | DragPayloadSymmetrical) => void
      unassignRegulationObjectGroup: (levelId: string, regulationObjectCode: string) => void
      assignedGroupUuids: Set<string>
    }

type UseBidLadderStateOptions = {
  /** Whether the market is symmetrical (e.g., FCR-N) */
  isSymmetrical: boolean
  /** The selected cascade market */
  selectedMarket: CascadeMarketId
  /** Whether to initialize volume capacity configuration (for FCR markets without activation groups) */
  withVolumeCapacity?: boolean
}

/**
 * Unified hook for bid ladder state that handles both directional and symmetrical markets.
 * Returns the appropriate state based on the isSymmetrical flag.
 *
 * Note: Both underlying hooks are always called (React hook rules), but only the
 * relevant one's state is used based on isSymmetrical.
 */
export function useBidLadderState(options: UseBidLadderStateOptions): UseBidLadderStateReturn {
  const { isSymmetrical, selectedMarket, withVolumeCapacity = false } = options
  const { maxBidLevels, defaultPrices } = getBidLevelConfig(selectedMarket)
  const directionalState = useBidLadderInputState({ defaultPrices, maxBidLevels, withVolumeCapacity })
  const symmetricalState = useBidLadderInputStateSymmetrical({ defaultPrices, maxBidLevels, withVolumeCapacity })

  return useMemo(() => {
    if (isSymmetrical) {
      return {
        isSymmetrical: true as const,
        state: symmetricalState,
        assignGroups: (levelId: string, payload: DragPayload | DragPayloadSymmetrical) => {
          symmetricalState.assignGroups(levelId, payload as DragPayloadSymmetrical)
        },
        unassignRegulationObjectGroup: symmetricalState.unassignRegulationObjectGroup,
        assignedGroupUuids: symmetricalState.assignedGroupUuids,
      }
    }

    return {
      isSymmetrical: false as const,
      state: directionalState,
      assignGroups: (levelId: string, payload: DragPayload | DragPayloadSymmetrical) => {
        directionalState.assignGroups(levelId, payload as DragPayload)
      },
      unassignRegulationObjectGroup: directionalState.unassignRegulationObjectGroup,
      assignedGroupUuids: directionalState.assignedGroupUuids,
    }
  }, [isSymmetrical, directionalState, symmetricalState])
}
